import { CheckSquare, Loader2, Plus, Square, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { Section, cx } from "./wf";
import { api } from "../lib/api";

/** Punch list on a job: quick add, click to check items off. Open items first. */
export function PunchList({ wid, items, canEdit, onUpdated, onDelete }) {
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const done = items.filter((e) => e.completed).length;
  const sorted = [...items].sort((a, b) => Number(!!a.completed) - Number(!!b.completed));

  const add = async (e) => {
    e.preventDefault();
    const description = text.trim();
    if (!description || adding) return;
    setAdding(true);
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch`, { method: "POST", body: { description } }));
      setText("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };
  const toggle = async (e) => {
    setBusyId(e.id);
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch/${e.id}/complete`, { method: "POST", body: { progress: e.completed ? 0 : 1 } }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="entries-section-punch">
      <Section title={`Punch List (${items.length ? `${done} of ${items.length} done` : 0})`} />
      {sorted.map((e) => (
        <div key={e.id} data-testid={`punch-item-${e.id}`} className="mb-2 flex items-center gap-2 rounded-xl border border-wf-line bg-wf-s2 px-3 py-2">
          <button data-testid={`punch-check-${e.id}`} onClick={() => toggle(e)} disabled={busyId === e.id} aria-checked={!!e.completed} role="checkbox"
            className="flex min-w-0 flex-1 items-center gap-3 text-left">
            {busyId === e.id ? <Loader2 size={22} className="animate-spin text-green-600" /> : e.completed ? <CheckSquare size={22} className="shrink-0 text-green-600" /> : <Square size={22} className="shrink-0 text-wf-mute" />}
            <span className="min-w-0">
              <span className={cx("block font-semibold", e.completed && "text-wf-mute line-through")}>{e.description}</span>
              <span className="block text-[11px] text-wf-mute">
                {e.completed && e.completed_at ? `Done ${new Date(e.completed_at).toLocaleDateString()}${e.completed_by ? ` by ${e.completed_by}` : ""}` : e.created_by ? `Added by ${e.created_by}` : ""}
              </span>
            </span>
          </button>
          {canEdit(e) && (
            <button data-testid={`punch-delete-${e.id}`} onClick={() => onDelete(e.id)} className="rounded-full p-2 text-wf-mute hover:bg-red-50 hover:text-wf-err"><Trash2 size={17} /></button>
          )}
        </div>
      ))}
      <form onSubmit={add} className="flex gap-2">
        <input data-testid="punch-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a punch list item"
          className="h-12 flex-1 rounded-xl border border-wf-line bg-wf-s3 px-4 text-[15px] outline-none focus:border-wf-brand focus:bg-wf-surface" />
        <button data-testid="punch-add-button" type="submit" disabled={!text.trim() || adding} className="flex h-12 w-12 items-center justify-center rounded-xl bg-wf-brand text-wf-onbrand disabled:opacity-40">
          {adding ? <Loader2 size={20} className="animate-spin" /> : <Plus size={22} />}
        </button>
      </form>
    </div>
  );
}
